import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../../hooks/useTranslate';
import { getTrainingVideoUrl } from '../../services/trainingVideoService';
import type { TrainingVideo } from '../../types/training';

interface VideoPlayerModalProps {
    videoId: string | null;
    allVideoIds?: string[];
    isOpen: boolean;
    onClose: () => void;
    onSelectVideo?: (videoId: string) => void;
}

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
    videoId,
    allVideoIds = [],
    isOpen,
    onClose,
    onSelectVideo,
}) => {
    const { t } = useTranslate();
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [videoMeta, setVideoMeta] = useState<TrainingVideo | null>(null);
    const [videoLoading, setVideoLoading] = useState(false);
    const [videoError, setVideoError] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Fetch signed URL + metadata when videoId changes or modal opens
    useEffect(() => {
        if (!isOpen || !videoId) {
            setVideoUrl(null);
            setVideoMeta(null);
            setVideoError(null);
            setIsPlaying(false);
            return;
        }

        let cancelled = false;
        setVideoLoading(true);
        setVideoError(null);

        getTrainingVideoUrl(videoId)
            .then(({ url, video }) => {
                if (!cancelled) {
                    setVideoUrl(url);
                    setVideoMeta(video);
                    setVideoLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setVideoError('Failed to load video');
                    setVideoLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [isOpen, videoId]);

    // Focus close button on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => closeBtnRef.current?.focus(), 0);
        }
    }, [isOpen]);

    // Escape to close
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (!isOpen) return;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = originalOverflow; };
    }, [isOpen]);

    // Navigation
    const currentIndex = videoId ? allVideoIds.indexOf(videoId) : -1;
    const prevVideoId = currentIndex > 0 ? allVideoIds[currentIndex - 1] : null;
    const nextVideoId = currentIndex >= 0 && currentIndex < allVideoIds.length - 1 ? allVideoIds[currentIndex + 1] : null;

    const handleNav = useCallback((targetId: string | null) => {
        if (targetId && onSelectVideo) onSelectVideo(targetId);
    }, [onSelectVideo]);

    if (!isOpen || !videoId) return null;

    const title = videoMeta?.title ?? '';

    return createPortal(
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="video-player-title"
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            {/* Dialog */}
            <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <h2 id="video-player-title" className="text-lg font-bold text-white truncate pr-4">
                        🎬 {videoLoading ? t('Loading...') : title}
                    </h2>
                    <button
                        ref={closeBtnRef}
                        onClick={onClose}
                        className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center text-white transition-all duration-200 hover:scale-105 flex-shrink-0"
                        aria-label={t('Close')}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
                    {/* Video player */}
                    <div className="aspect-video bg-gray-900 relative">
                        {videoLoading ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60 text-sm gap-3">
                                <div className="w-10 h-10 border-3 border-white/20 border-t-white/70 rounded-full animate-spin" />
                                {t('Loading video...')}
                            </div>
                        ) : videoError ? (
                            <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">
                                {t(videoError)}
                            </div>
                        ) : videoUrl ? (
                            <>
                                <video
                                    ref={videoRef}
                                    key={videoUrl}
                                    className="w-full h-full"
                                    controls
                                    controlsList="nodownload"
                                    disablePictureInPicture
                                    onContextMenu={e => e.preventDefault()}
                                    onPlay={() => setIsPlaying(true)}
                                    onPause={() => setIsPlaying(false)}
                                    onEnded={() => setIsPlaying(false)}
                                    src={videoUrl}
                                >
                                    {t('Your browser does not support the video element.')}
                                </video>
                                {!isPlaying && (
                                    <button
                                        type="button"
                                        onClick={() => videoRef.current?.play()}
                                        className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity hover:bg-black/30"
                                        aria-label={t('Play video')}
                                    >
                                        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg transition-transform hover:scale-110">
                                            <svg className="w-8 h-8 text-red-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                                <svg className="w-12 h-12 text-white/40 mr-3" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                {t('Video not available')}
                            </div>
                        )}
                    </div>

                    {/* Details */}
                    {videoMeta && (
                        <div className="p-6 space-y-4">
                            <p className="text-gray-700 text-sm leading-relaxed">{videoMeta.description}</p>
                            <div className="flex flex-wrap items-center gap-3 text-xs">
                                <span className="text-gray-400">
                                    📂 {videoMeta.category}
                                </span>
                                <span className="text-gray-400">
                                    ⏱ {formatDuration(videoMeta.durationSeconds)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer navigation */}
                {allVideoIds.length > 1 && (
                    <div className="border-t px-6 py-3 flex items-center justify-between flex-shrink-0 bg-gray-50">
                        <button
                            type="button"
                            disabled={!prevVideoId}
                            onClick={() => handleNav(prevVideoId)}
                            className="text-sm font-medium text-[#D31145] disabled:text-gray-300 disabled:cursor-not-allowed hover:underline"
                        >
                            ◀ {t('Previous')}
                        </button>
                        <button
                            type="button"
                            disabled={!nextVideoId}
                            onClick={() => handleNav(nextVideoId)}
                            className="text-sm font-medium text-[#D31145] disabled:text-gray-300 disabled:cursor-not-allowed hover:underline"
                        >
                            {t('Next')} ▶
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default VideoPlayerModal;
